import type { Rating } from "openskill";

import type { ActivityId, BetId, MatchId, MatchResultId, PlayerId } from "./branded";

export type Player = {
  id: PlayerId;
  name: string;
  rating: Rating;
  totalPoints: number;

  stats: {
    gamesPlayed: number;
    winStreak: number;
  };

  bettingBankroll: number;

  bettingStats: {
    totalBets: number;
    correctBets: number;
    netEarnings: number;
  };

  personalBests: {
    highestPoints: number;
    longestStreak: number;
    bestRating: number;
  };
};

/** Point weight for an activity — set by organizer, hidden from players. */
export enum Weight {
  Low = 0.5,
  Normal = 1,
  High = 1.5,
  Double = 2,
}

/** Player-friendly variance label — maps to beta internally */
export enum ActivityVariance {
  Low,
  Medium,
  High,
}

/** The format for how an activity is played */
export enum ActivityFormat {
  FreeForAll,
  Teams,
  Tournament,
}

export enum TournamentFormat {
  Swiss = 0,
  RoundRobin = 1,
  SingleElimination = 2,
  DoubleElimination = 3,
}

type record = Record<never, never>;

type BaseActivity<TFormatProps = record> = {
  id: ActivityId;
  name: string;
  /** Internal beta value for TrueSkill — hidden from players */
  beta: number;
  /** Point weight — hidden from players, set by organizer */
  weight: Weight;
  /** Player-friendly variance label */
  variance: ActivityVariance;
  /** How the activity is played */
  format: ActivityFormat;
  /** Which players participate (empty = all) */
  participantIds: PlayerId[];
} & TFormatProps;

export type FreeForAllActivity = BaseActivity & {
  format: ActivityFormat.FreeForAll;
  /** How many positions to consider for ranking (0 = all) */
  topK: number;
};

export type TeamsActivity = BaseActivity & {
  format: ActivityFormat.Teams;
  /** Number of teams */
  numTeams: number;
  /** Players per team — one entry per team */
  teamSizes: number[];
  /** Randomly assign players to teams */
  randomizeTeams: boolean;
  /** How many positions to consider for ranking (0 = all) */
  topK: number;
};

export type TournamentActivity = BaseActivity & {
  format: ActivityFormat.Tournament;
  tournamentFormat: TournamentFormat;
  knockoutEliminations?: number;
};

export type Activity = FreeForAllActivity | TeamsActivity | TournamentActivity;

export const isFreeForAll = (activity: Activity | undefined): activity is FreeForAllActivity =>
  activity?.format === ActivityFormat.FreeForAll;

export const isTeams = (activity: Activity | undefined): activity is TeamsActivity =>
  activity?.format === ActivityFormat.Teams;

export const isTournament = (activity: Activity | undefined): activity is TournamentActivity =>
  activity?.format === ActivityFormat.Tournament;

/** Persistent multiplier configuration (admin-configurable) */
export type MultiplierConfig = {
  scale: {
    enabled: boolean;
    factor: number;
  };
  weight: {
    enabled: boolean;
  };
  upset: {
    enabled: boolean;
  };
  streak: {
    enabled: boolean;
  };
  fatigue: {
    enabled: boolean;
    halfLife: number;
  };
  momentum: {
    enabled: boolean;
    factor: number;
    cap: number;
  };
  comeback: {
    enabled: boolean;
    threshold: number;
    maxBonus: number;
  };
  consistency: {
    enabled: boolean;
    sigmaThreshold: number;
    bonus: number;
  };
  closeness: {
    enabled: boolean;
    maxDelta: number;
    bonus: number;
  };
  variety: {
    enabled: boolean;
    decay: number;
    bonus: number;
  };
  rivalry: {
    enabled: boolean;
    lookback: number;
    bonus: number;
  };
};

// ── Match ──────────────────────────────────────────────────────

export type Match = {
  id: MatchId;
  activityId: ActivityId;
  teams: PlayerId[][];
  ranks: number[];
  createdAt: number;
};

export type Breakdown = { base: number } & Record<string, number>;

export type TeamResult = {
  playerId: PlayerId;
  name: string;
  ratingBefore: Rating;
  ratingAfter: Rating;
  deltaMu: number;
  expectedWin: number;
  matchPoints: number;
  breakdown: Breakdown;
};

export type MatchResult = {
  id: MatchResultId;
  matchId: MatchId;
  activityId: ActivityId;
  teamResults: {
    teamIndex: number;
    rank: number;
    players: TeamResult[];
  }[];
};

// ── Betting ────────────────────────────────────────────────────

export type Bet = {
  id: BetId;
  /** The player placing the bet */
  bettorId: PlayerId;
  /** Which activity this bet is about */
  activityId: ActivityId;
  /** Who the bettor thinks will win / place first */
  predictedWinnerId: PlayerId;
  /** Points wagered from bankroll */
  wager: number;
  /** System-generated odds based on ratings */
  odds: number;
  createdAt: number;
  resolved?: boolean;
  correct?: boolean;
  /** Points earned (positive) or lost (negative) from this bet */
  payout?: number;
};

// ── Status line (for TV) ───────────────────────────────────────

export type StatusEvent = {
  text: string;
  timestamp: number;
};
